/* eslint-disable import/no-unresolved */
import React, { Component } from "react";
import { bool, shape, arrayOf, string, number } from "prop-types";
import classNames from "classnames";

import {
    Box,
    FormattedAmount as FormattedAmountSB,
    Text,
    FormattedDate as FormattedDateSB,
} from "@ui-kit/components/index.es";

import Container from "pages/_components/Container";
import I18n from "pages/_components/I18n";
import Col from "react-bootstrap/lib/Col";
import FormattedAmount from "pages/_components/FormattedAmount";
import EquivalentTotalBalance from "pages/metrocorp/_components/EquivalentTotalBalance";
import FormattedDate from "pages/_components/FormattedDate";
import Image from "pages/_components/Image";
import CardContainer from "pages/_components/Card/CardContainer";
import Card from "pages/_components/Card/Card";

import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import { sortByPredefinedOrder } from "util/array";
import moment from "moment";

const FORM_ID = "metrocorp.list";

const groupHoldingsByConfig = (holdings, config) =>
    holdings.reduce((acc, holding) => {
        const tipoOriginal = holding.tipoEspecie || "";
        const tipo = tipoOriginal.trim() || "4";
        const configEntry = config[tipo] || config.DEFAULT;
        const desc = configEntry?.desc || "OTRO";

        const holdingToAdd =
            desc === "RENTA FIJA"
                ? { ...holding, descripcionEspecie: `${holding.codEspe} - ${holding.descripcionEspecie}` }
                : holding;

        const currentGroup = acc[desc] || [];

        return {
            ...acc,
            [desc]: [...currentGroup, holdingToAdd],
        };
    }, {});

class MetrocorpHoldingList extends Component {
    static propTypes = {
        isDesktop: bool.isRequired,
        holdings: arrayOf(shape({})).isRequired,
        currentDate: shape({}).isRequired,
        currency: string.isRequired,
        filtersData: shape({
            principalAccount: number.isRequired,
        }).isRequired,
    };

    renderNoResults = () => (
        <>
            <div className="text-center no-more-data px-3 mt-2">
                <div className="illustration-wrapper">
                    <Image src="images/loupeInFile.svg" className="svg-big-icon" />
                </div>
                <p className="text-lead">
                    <I18n id={`${FORM_ID}.empty.message`} />
                </p>
            </div>
        </>
    );

    renderListForHolding = (itemList, title) => {
        const currency = "0";
        const sumFunction = (accumulator, holding) => accumulator + holding.saldoValorizadoPesos;
        const total = itemList.reduce(sumFunction, 0);

        const { isDesktop } = this.props;
        return (
            <>
                {itemList.length > 0 ? (
                    <Container className="flex-grow scrollable">
                        <Col md={12} className="with-table-header-margin px-0">
                            <I18n id={`${FORM_ID}.holding.${title}.title`} component="h4" />
                            <div className="w-100">
                                {itemList.map((item) => this.renderListForHoldingItem(item))}
                                <CardContainer className="metrocorp__card-container metrocorp__holding-container">
                                    <Card
                                        className="metrocorp__card no-hr"
                                        rightContent={
                                            <div className="metrocorp__card-right">
                                                <div className="metrocorp__totalcash">
                                                    <I18n
                                                        id={`${FORM_ID}.holding.${title}.balanceTotal`}
                                                        componentProps={{
                                                            className: "metrocorp__totalcash-info",
                                                        }}
                                                    />
                                                    <FormattedAmount
                                                        currency={currency}
                                                        className={
                                                            total >= 0
                                                                ? "metrocorp__totalcash-data"
                                                                : "metrocorp__totalcash-data metrocorp__red"
                                                        }
                                                        quantity={total}
                                                    />
                                                </div>
                                            </div>
                                        }
                                    />
                                </CardContainer>
                                {!isDesktop && <hr className="metrocorp__holding-separator" />}
                            </div>
                        </Col>
                    </Container>
                ) : (
                    <></>
                )}
            </>
        );
    };

    renderListForHoldingDynamic = (itemList, categoryTitle) => {
        const currency = "0";
        const sumFunction = (accumulator, holding) => accumulator + holding.saldoValorizadoPesos;
        const total = itemList.reduce(sumFunction, 0);

        const { isDesktop } = this.props;
        return (
            <>
                {itemList.length > 0 ? (
                    <Container className="flex-grow scrollable">
                        <Col md={12} className="with-table-header-margin px-0">
                            <h4>{categoryTitle}</h4>
                            <div className="w-100">
                                {itemList.map((item) => this.renderListForHoldingItem(item))}
                                <CardContainer className="metrocorp__card-container metrocorp__holding-container">
                                    <Card
                                        className="metrocorp__card no-hr"
                                        rightContent={
                                            <div className="metrocorp__card-right">
                                                <div className="metrocorp__totalcash">
                                                    <span className="metrocorp__totalcash-info">
                                                        {i18nUtils.get(
                                                            `${FORM_ID}.holding.dynamic.balanceTotal`,
                                                            `Total ${categoryTitle}`,
                                                        )}
                                                    </span>
                                                    <FormattedAmount
                                                        currency={currency}
                                                        className={
                                                            total >= 0
                                                                ? "metrocorp__totalcash-data"
                                                                : "metrocorp__totalcash-data metrocorp__red"
                                                        }
                                                        quantity={total}
                                                    />
                                                </div>
                                            </div>
                                        }
                                    />
                                </CardContainer>
                                {!isDesktop && <hr className="metrocorp__holding-separator" />}
                            </div>
                        </Col>
                    </Container>
                ) : (
                    <></>
                )}
            </>
        );
    };

    renderListForHoldingItem = (item) => {
        const { isDesktop } = this.props;
        const { codEspe, valorUnitario, saldo, valorPromedio, saldoValorizadoPesos, tipoEspecie } = item;
        const enableNewTotal = configUtils.getBoolean("frontend.show.metrocorp.Tenencias.functionalities", false);
        const currency = "0";
        const isFCI = tipoEspecie === "8";

        if (isDesktop) {
            return (
                <>
                    <CardContainer className="metrocorp__card-container">
                        <Card
                            className="metrocorp__card"
                            title={
                                <div className="metrocorp__card-title">
                                    <div className="metrocorp__specie">
                                        <I18n
                                            id={`${FORM_ID}.holding.column.species`}
                                            componentProps={{ className: "metrocorp__specie-info" }}
                                        />
                                        <span className="metrocorp__specie-data">{codEspe}</span>
                                    </div>
                                </div>
                            }
                            content={
                                <div className="metrocorp__card-content">
                                    <div className="metrocorp__description">
                                        <span className="metrocorp__description-data">{item?.descripcionEspecie}</span>
                                    </div>
                                </div>
                            }
                            bottom={
                                !enableNewTotal ? (
                                    <div className="metrocorp__card-bottom">
                                        <div
                                            className={
                                                saldo !== null || valorPromedio !== null
                                                    ? "metrocorp__unit-value metrocorp__separator"
                                                    : "metrocorp__unit-value"
                                            }>
                                            <I18n
                                                id={`${FORM_ID}.holding.column.unitValue`}
                                                componentProps={{ className: "metrocorp__unit-value-info" }}
                                            />
                                            <FormattedAmount
                                                currency={currency}
                                                className={
                                                    valorUnitario >= 0
                                                        ? "metrocorp__unit-value-data"
                                                        : "metrocorp__unit-value-data metrocorp__red"
                                                }
                                                quantity={valorUnitario}
                                                notBold
                                            />
                                        </div>
                                        <div
                                            className={
                                                valorPromedio !== null
                                                    ? "metrocorp__balance metrocorp__separator"
                                                    : "metrocorp__balance"
                                            }>
                                            <I18n
                                                id={`${FORM_ID}.holding.column.balance`}
                                                componentProps={{ className: "metrocorp__balance-info" }}
                                            />
                                            <FormattedAmount
                                                noCurrency
                                                className={
                                                    saldo >= 0
                                                        ? "metrocorp__balance-data"
                                                        : "metrocorp__balance-data metrocorp__red"
                                                }
                                                quantity={saldo}
                                                notBold
                                            />
                                        </div>
                                        <div className="metrocorp__average">
                                            <I18n
                                                id={`${FORM_ID}.holding.column.averagePrice`}
                                                componentProps={{ className: "metrocorp__average-info" }}
                                            />
                                            <FormattedAmount
                                                noCurrency
                                                className={
                                                    valorPromedio >= 0
                                                        ? "metrocorp__average-data"
                                                        : "metrocorp__average-data metrocorp__red"
                                                }
                                                quantity={valorPromedio}
                                                notBold
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="metrocorp__card-bottom">
                                        <div
                                            className={classNames("metrocorp__balance", {
                                                metrocorp__separator: valorUnitario,
                                            })}>
                                            <I18n
                                                id={`${FORM_ID}.holding.column.nominalvalue${isFCI ? ".fci" : ""}`}
                                                componentProps={{ className: "metrocorp__balance-info" }}
                                                defaultValue={isFCI ? "Cuotapartes" : "Valor Nominal"}
                                            />
                                            <FormattedAmount
                                                noCurrency
                                                className={
                                                    saldo >= 0
                                                        ? "metrocorp__balance-data"
                                                        : "metrocorp__balance-data metrocorp__red"
                                                }
                                                quantity={saldo}
                                                notBold
                                            />
                                        </div>
                                        <div className="metrocorp__unit-value">
                                            <I18n
                                                id={`${FORM_ID}.holding.column.currentvalue${isFCI ? ".fci" : ""}`}
                                                componentProps={{ className: "metrocorp__unit-value-info" }}
                                                defaultValue={isFCI ? "Valor de cuotas" : "Precio Actual"}
                                            />
                                            <FormattedAmount
                                                currency={currency}
                                                className={
                                                    valorUnitario >= 0
                                                        ? "metrocorp__unit-value-data"
                                                        : "metrocorp__unit-value-data metrocorp__red"
                                                }
                                                quantity={valorUnitario}
                                                notBold
                                            />
                                        </div>
                                    </div>
                                )
                            }
                            rightContent={
                                <div className="metrocorp__card-right">
                                    <div className="metrocorp__totalamount">
                                        <I18n
                                            id={`${FORM_ID}.holding.column.balanceValuedInCurrency`}
                                            componentProps={{ className: "metrocorp__totalamount-info" }}
                                        />
                                        <FormattedAmount
                                            currency={currency}
                                            className={
                                                saldoValorizadoPesos >= 0
                                                    ? "metrocorp__totalamount-data"
                                                    : "metrocorp__totalamount-data metrocorp__red"
                                            }
                                            quantity={saldoValorizadoPesos}
                                            notBold
                                        />
                                    </div>
                                </div>
                            }
                        />
                    </CardContainer>
                </>
            );
        }
        return (
            <>
                <CardContainer className="metrocorp__card-container">
                    <Card
                        className="metrocorp__card mt-0"
                        title={
                            <div className="metrocorp__card-title">
                                <div className="metrocorp__specie">
                                    <I18n
                                        id={`${FORM_ID}.holding.column.species`}
                                        componentProps={{ className: "metrocorp__specie-info" }}
                                    />
                                    <span className="metrocorp__specie-data">{codEspe}</span>
                                </div>
                                <div className="metrocorp__description">
                                    <span className="metrocorp__description-data">{item?.descripcionEspecie}</span>
                                </div>
                                <hr className="mr-0" />
                            </div>
                        }
                        content={
                            !enableNewTotal ? (
                                <div className="metrocorp__card-content">
                                    <div className="metrocorp__unit-value">
                                        <I18n
                                            id={`${FORM_ID}.holding.column.unitValue`}
                                            componentProps={{ className: "metrocorp__unit-value-info" }}
                                        />
                                        <FormattedAmount
                                            currency={currency}
                                            className={
                                                valorUnitario >= 0
                                                    ? "metrocorp__unit-value-data"
                                                    : "metrocorp__unit-value-data metrocorp__red"
                                            }
                                            quantity={valorUnitario}
                                            notBold
                                        />
                                    </div>
                                    <div className="metrocorp__balance">
                                        <I18n
                                            id={`${FORM_ID}.holding.column.balance`}
                                            componentProps={{ className: "metrocorp__balance-info" }}
                                        />
                                        <FormattedAmount
                                            noCurrency
                                            className={
                                                saldo >= 0
                                                    ? "metrocorp__balance-data"
                                                    : "metrocorp__balance-data metrocorp__red"
                                            }
                                            quantity={saldo}
                                            notBold
                                        />
                                    </div>
                                    <div className="metrocorp__average">
                                        <I18n
                                            id={`${FORM_ID}.holding.column.averagePrice`}
                                            componentProps={{ className: "metrocorp__average-info" }}
                                        />
                                        <FormattedAmount
                                            currency={currency}
                                            className={
                                                valorPromedio >= 0
                                                    ? "metrocorp__average-data"
                                                    : "metrocorp__average-data metrocorp__red"
                                            }
                                            quantity={valorPromedio}
                                            notBold
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="metrocorp__card-content">
                                    <div className="metrocorp__balance">
                                        <I18n
                                            id={`${FORM_ID}.holding.column.nominalvalue${isFCI ? ".fci" : ""}`}
                                            componentProps={{ className: "metrocorp__balance-info" }}
                                            defaultValue={isFCI ? "Cuotapartes" : "Valor Nominal"}
                                        />
                                        <FormattedAmount
                                            noCurrency
                                            className={
                                                saldo >= 0
                                                    ? "metrocorp__balance-data"
                                                    : "metrocorp__balance-data metrocorp__red"
                                            }
                                            quantity={saldo}
                                            notBold
                                        />
                                    </div>
                                    <div className="metrocorp__unit-value">
                                        <I18n
                                            id={`${FORM_ID}.holding.column.currentvalue${isFCI ? ".fci" : ""}`}
                                            componentProps={{ className: "metrocorp__unit-value-info" }}
                                            defaultValue={isFCI ? "Valor de cuotas" : "Precio Actual"}
                                        />
                                        <FormattedAmount
                                            currency={currency}
                                            className={
                                                valorUnitario >= 0
                                                    ? "metrocorp__unit-value-data"
                                                    : "metrocorp__unit-value-data metrocorp__red"
                                            }
                                            quantity={valorUnitario}
                                            notBold
                                        />
                                    </div>
                                </div>
                            )
                        }
                        rightContent={
                            <div className="metrocorp__card-right">
                                <div className="metrocorp__totalamount">
                                    <I18n
                                        id={`${FORM_ID}.holding.column.balanceValuedInCurrency`}
                                        componentProps={{ className: "metrocorp__totalamount-info" }}
                                    />
                                    <FormattedAmount
                                        currency={currency}
                                        className={
                                            saldoValorizadoPesos >= 0
                                                ? "metrocorp__totalamount-data"
                                                : "metrocorp__totalamount-data metrocorp__red"
                                        }
                                        quantity={saldoValorizadoPesos}
                                        notBold
                                    />
                                </div>
                            </div>
                        }
                    />
                </CardContainer>
            </>
        );
    };

    renderHoldingsSection = (
        enableDynamicCategories,
        groupedHoldings,
        cashList,
        fixedRentList,
        variableRentList,
        others,
    ) => {
        if (enableDynamicCategories) {
            if (groupedHoldings && Object.keys(groupedHoldings).length > 0) {
                return <>{this.renderDynamicCategories(groupedHoldings)}</>;
            }
            return <>{this.renderNoResults()}</>;
        }

        if (cashList.length > 0 || fixedRentList.length >= 0 || variableRentList.length >= 0) {
            return (
                <>
                    {this.renderListForHolding(cashList, "cash")}
                    {fixedRentList.length > 0 && this.renderListForHolding(fixedRentList, "fixedRent")}
                    {variableRentList.length > 0 && this.renderListForHolding(variableRentList, "variableRent")}
                    {others.length > 0 && this.renderListForHolding(others, "others")}
                </>
            );
        }

        return <>{this.renderNoResults()}</>;
    };

    renderDynamicCategories = (groupedHoldings) => {
        const categoryOrderStr = configUtils.get("metrocorp.tenencia.order", "CASH|RENTA FIJA|RENTA VARIABLE|OTRO|FCI");
        const categoryOrder = categoryOrderStr.split("|");
        const sortedCategories = sortByPredefinedOrder(Object.keys(groupedHoldings), categoryOrder);

        return sortedCategories.map(
            (category) =>
                groupedHoldings[category].length > 0 &&
                this.renderListForHoldingDynamic(groupedHoldings[category], category),
        );
    };

    render() {
        const { holdings, isDesktop, currentDate, currency, filtersData } = this.props;
        const enableNewTotal = configUtils.getBoolean("frontend.show.metrocorp.Tenencias.functionalities", false);
        const enableDynamicCategories = configUtils.getBoolean("metrocorp.tenencia.tipo.functionalities", false);

        if (!holdings) {
            return null;
        }

        let groupedHoldings = null;
        let cashList = [];
        let fixedRentList = [];
        let variableRentList = [];
        let others = [];

        if (enableDynamicCategories) {
            const tenenciaConfigStr = configUtils.get("metrocorp.tenencia.tipo", "{}");
            const tenenciaConfig = JSON.parse(tenenciaConfigStr);
            groupedHoldings = groupHoldingsByConfig(holdings, tenenciaConfig);
        } else {
            cashList = holdings.filter((holding) => holding.tipoEspecie === " " || holding.tipoEspecie === "4");
            fixedRentList = holdings
                .filter((holding) => holding.tipoEspecie === "1")
                .map((holding) => {
                    const descripcionEspecie = `${holding.codEspe} - ${holding.descripcionEspecie}`;
                    const newHolding = { ...holding, descripcionEspecie };
                    return newHolding;
                });
            variableRentList = holdings.filter((holding) => holding.tipoEspecie === "2");
            others = holdings.filter(
                (holding) =>
                    holding.tipoEspecie === "0" ||
                    holding.tipoEspecie === "3" ||
                    holding.tipoEspecie === "5" ||
                    holding.tipoEspecie === "8",
            );
        }

        const sumFunction = (accumulator, holding) => accumulator + holding.saldoValorizadoPesos;

        const accountTotal = holdings.reduce(sumFunction, 0);
        return (
            <>
                {!enableNewTotal ? (
                    <Container rowClassName="metrocorp__total-acount">
                        <EquivalentTotalBalance
                            balance={accountTotal}
                            title="metrocorp.balance.accountTotal"
                            bold
                            smallAmount
                        />
                        {!isDesktop && (
                            <Col xs={12} className="d-flex font-light">
                                <I18n
                                    id="metrocorp.list.toTheDate"
                                    component="p"
                                    componentProps={{
                                        className: "font-light my-0 mr-2",
                                    }}
                                />
                                <FormattedDate date={currentDate} />
                            </Col>
                        )}
                    </Container>
                ) : (
                    <Container rowClassName="">
                        <Box
                            background="primary-background-color2"
                            flex
                            my={!isDesktop ? 8 : 24}
                            mx={!isDesktop ? 16 : 0}
                            py={12}
                            px={16}
                            gap={40}
                            radius={8}
                            justify="between"
                            shadow="default"
                            w={!isDesktop ? "100%" : undefined}>
                            <Box
                                flex
                                gap={8}
                                align={isDesktop ? "center" : "start"}
                                justify={!isDesktop ? "center" : "start"}
                                column={!isDesktop}>
                                <Box background="primary-background-color" flex p={4} px={8} radius={2}>
                                    <Text variant="captionBold" color="text-inverse">
                                        {i18nUtils.get("metrocorp.list.holding.account", "Cuenta")}
                                    </Text>
                                </Box>
                                <Text variant="body" css={{ fontSize: isDesktop ? 20 : 18 }}>
                                    {filtersData?.principalAccount}
                                </Text>
                            </Box>
                            <Box column align="end" gap={4}>
                                <Box column align="end">
                                    <Text variant="captionBold">{i18nUtils.get("metrocorp.balance.accountTotal")}</Text>
                                    <FormattedAmountSB
                                        amount={accountTotal}
                                        currency={currency || i18nUtils.get("currency.label.ARS")}
                                        css={{ fontSize: isDesktop ? 20 : 18 }}
                                    />
                                </Box>
                                <Text
                                    variant="caption"
                                    css={{ display: "flex", flexFlow: "nowrap", textWrap: "nowrap", gap: 4 }}>
                                    {i18nUtils.get("metrocorp.list.holding.date", "Valorizado al")}{" "}
                                    <FormattedDateSB
                                        date={
                                            moment().format("DD/MM/yyyy") === moment(currentDate).format("DD/MM/yyyy")
                                                ? moment(currentDate)
                                                      .subtract(new Date(currentDate).getDay() === 1 ? 3 : 1, "days")
                                                      .toDate()
                                                : currentDate
                                        }
                                        variant="caption"
                                        locale="es-ES"
                                    />
                                </Text>
                            </Box>
                        </Box>
                    </Container>
                )}
                {this.renderHoldingsSection(
                    enableDynamicCategories,
                    groupedHoldings,
                    cashList,
                    fixedRentList,
                    variableRentList,
                    others,
                )}
            </>
        );
    }
}

export default MetrocorpHoldingList;
